import axios from "axios";

let globalLogoutHandler = () => {};
let lockUserHandler = () => {};
// Показывает модалку повторной верификации и резолвится, когда пользователь
// успешно подтвердил личность; реджектится, если он отменил (→ логаут).
let reverifyHandler: () => Promise<void> = () => Promise.reject();

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalLogoutHandler = handler;
};

export const setLockUserHandler = (handler: () => void) => {
  lockUserHandler = handler;
};

export const setReverifyHandler = (handler: () => Promise<void>) => {
  reverifyHandler = handler;
};

const handleLogin = () => {
  globalLogoutHandler();
};

const handleLockUser = () => {
  lockUserHandler();
};

// Отдельный axios-инстанс БЕЗ интерцептора: вызовы refresh/reverify не должны
// повторно перехватываться обработчиком 401 (иначе — бесконечная рекурсия).
// Тот же origin → HttpOnly-cookie (access/refresh) уходят автоматически.
export const bareAxios = axios.create();
bareAxios.defaults.headers.common.Accept = "application/json";

const REFRESH_URL = `/api/auth/refresh/`;

// Продление токенов. Ре-верификация сюда больше не приходит: бэк отвечает
// reverify_required на самих запросах админа, а refresh продлевает молча даже
// заблокированную сессию — см. FRONTEND_ADMIN_REVERIFY.md в garant-backend.
async function callRefresh(): Promise<boolean> {
  const res = await bareAxios.post(
    REFRESH_URL,
    {},
    { validateStatus: () => true }
  );
  return res.status === 200;
}

// Single-flight: параллельные 401 разделяют один refresh, а не шлют пачку.
let refreshInFlight: Promise<boolean> | null = null;

function ensureFreshTokens(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = callRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// --- Вход по ссылке из Telegram-бота -----------------------------------------
// Бот присылает ссылку вида /auth/login?code=..., и переход по ней — это уже
// сильная аутентификация: код человек получил в Telegram и «ввёл» самим
// переходом. Бэк так и считает — обмен кода на сессию сразу открывает окно
// активности (seed_privileged_session в garant-backend).
//
// Проблема в том, что до обмена в браузере лежит СТАРАЯ cookie — с прошлого
// входа в этой же вкладке. Если та сессия простояла дольше окна, первый же
// запрос панели (профиль на старте) получит 403 reverify_required, и человек,
// только что пришедший по свежей ссылке, упрётся в запрос кода вместо входа.
// Причём запрос этот ни о чём: обменять код всё равно нельзя, пока модалка
// держит стартовый запрос, а закрыв её — человек спокойно входит по ссылке.
// Ровно это и выглядело как «код просит, но закрыл — и пропустило».
//
// Поэтому пока вход по ссылке не завершён, 403 reverify_required относится к
// старой сессии, а не к простою текущей, и кода мы не просим.
//
// Состояние считается один раз на загрузку страницы: переход по ссылке — это
// всегда полная загрузка, а не переход внутри SPA.
function hasLoginCodeInUrl(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("code");
}

let linkLoginPending = hasLoginCodeInUrl();

/**
 * Снять гейт: вход завершён, дальше 403 reverify_required — это простой.
 *
 * Зовётся из двух мест, и оба обязательны:
 *  - Login, когда обмен кода закончился (неважно, удачно или нет);
 *  - AuthInit, когда профиль пришёл сам — сессия в cookie оказалась рабочей,
 *    и Login с его обменом не смонтируется вовсе.
 * Без второго гейт застрял бы поднятым на всю жизнь вкладки и молча съел бы
 * окно кода после настоящего простоя.
 */
export const finishLinkLogin = () => {
  linkLoginPending = false;
};

// Single-flight для модалки кода. На дашборде параллельно летит десяток
// запросов, и после простоя все они получат 403 одновременно — модалка должна
// открыться одна, и код в Telegram уйти один.
let reverifyInFlight: Promise<boolean> | null = null;

/**
 * Открыть окно с кодом снаружи перехватчика.
 *
 * Нужна замку по простою: он запирает сессию сам, не дожидаясь, пока панель
 * сходит в API, и показать окно надо сразу — иначе человек вернётся к рабочей
 * с виду панели и упрётся в код на первом же клике. Single-flight общий с
 * перехватчиком: если запрос уже словил 403, окно будет одно на двоих.
 */
export function requestReverify(): Promise<boolean> {
  return ensureReverified();
}

function ensureReverified(): Promise<boolean> {
  if (!reverifyInFlight) {
    reverifyInFlight = reverifyHandler()
      .then(() => true)
      // Пользователь закрыл модалку, не подтвердив, — разлогинивать не за что,
      // просто отдаём исходную ошибку вызывающему коду.
      .catch(() => false)
      .finally(() => {
        reverifyInFlight = null;
      });
  }
  return reverifyInFlight;
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = "application/json";
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      return config;
    },
    (err: any) => Promise.reject(err)
  );
  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const original = error.config;
      const status = error.response?.status;

      // 401 → пробуем обновить токены (и, если нужно, повторно верифицироваться),
      // затем повторяем исходный запрос. Один повтор на запрос — иначе логаут.
      if (status === 401 && original) {
        if (original._retry) {
          // Уже повторяли после refresh, а всё равно 401 → сессия недействительна.
          handleLogin();
          return Promise.reject(error);
        }
        original._retry = true;
        const ok = await ensureFreshTokens();
        if (ok) {
          return axios(original);
        }
        handleLogin();
        return Promise.reject(error);
      }

      // Сессия простояла дольше окна — нужен код из Telegram. Это НЕ логаут:
      // сессия жива, человеку нужно только подтвердиться. Подтвердился —
      // повторяем упавший запрос, один раз.
      if (
        status === 403 &&
        error.response?.data?.code === "reverify_required" &&
        original &&
        !original._reverifyRetry
      ) {
        original._reverifyRetry = true;

        // Вход по ссылке ещё идёт: отказ пришёл старой сессии, и спрашивать
        // код у человека, который прямо сейчас входит, не за что. Запрос
        // отдаём с ошибкой, а не ждём обмена: стартовый профиль висит перед
        // сплэш-скрином, и Login, который этот обмен делает, смонтируется
        // только после того, как запрос завершится — ожидание тут было бы
        // взаимной блокировкой. Профиль перезапросится после входа сам.
        if (linkLoginPending) {
          return Promise.reject(error);
        }

        const verified = await ensureReverified();
        if (verified) {
          return axios(original);
        }
        return Promise.reject(error);
      }

      // Нет прав доступа (не сотрудник) → экран «нет доступа».
      if (status === 403 && error.response?.data?.code === "forbidden") {
        handleLockUser();
      }

      return Promise.reject(error);
    }
  );
}
